import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	createVehicle,
	deleteVehicle,
	getVehicles,
	updateVehicle,
} from "../../../reducers/VehiclesReducer";
import DeleteConfirmationDialog from "../../../components/DeleteConfirmationDialog";
import PATDeleteButton from "../../../components/Buttons/PATDeleteButton";
import PATEditButton from "../../../components/Buttons/PATEditButton";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	TableCell,
	TableRow,
} from "@mui/material";
import CustomTable from "../../../components/CustomTable";
import PATAddButton from "../../../components/Buttons/PATAddButton";
import { ExpandMore, DirectionsCar } from "@mui/icons-material";
import {
	capitalizeFirstLetter,
	getCodeDescription,
	getCodeId,
	getServiceStatus,
	mapDictionaryOptions,
	VehicleStatusBadge,
} from "../../../utils/functions";
import { getAccessPermission } from "../../../reducers/PermissionsReducer";
import AddEditVehicleDialog from "../../Vehicles/AddEditVehicle";
import {
	getEmployees,
	getFundingSources,
	getWorkOrders,
} from "../../../utils/commonServices";
import { notify } from "../../../reducers/NotifierReducer";

const tableHeaders = [
	"Unit#",
	"YEAR / MAKE / MODEL",
	"VIN",
	"Status",
	"ODOMETER",
	"DIVISION",
	"SERVICE",
	"Actions",
];

const VehiclesTable = (props) => {
	const { allExpanded, perPersonnelId, theme } = props;

	const dispatch = useDispatch();
	const userAgency = useSelector((state) => state.user.userAgency);
	const dictionary = useSelector((state) => state.dictionary.dictionary);
	const { userData } = useSelector((state) => state.user);

	const { vehicles } = useSelector((state) => state?.vehicles);

	const vehiclePermission = getAccessPermission("fleet", "Vehicles", "any");
	const canAddVehicle = vehiclePermission?.Create ?? true;
	const canEditVehicle = vehiclePermission?.Edit ?? true;
	const canDeleteVehicle = vehiclePermission?.Delete ?? true;

	const [expanded, setExpanded] = useState(allExpanded);
	const [selectedId, setSelectedId] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [openDialog, setOpenDialog] = useState(false);
	const [selectedVehicle, setSelectedVehicle] = useState(null);
	const [employees, setEmployees] = useState([]);
	const [fundingSources, setFundingSources] = useState([]);
	const [workOrders, setWorkOrders] = useState([]);

	const departmentOptions = mapDictionaryOptions(
		dictionary?.codePerDepartment,
	);
	const statusOptions = mapDictionaryOptions(
		dictionary?.codePerVehicleStatus,
	);

	useEffect(() => {
		setExpanded(allExpanded);
	}, [allExpanded]);

	const handleSaveData = async () => {
		const [empList, fundingList, workOrderList] = await Promise.all([
			getEmployees(userAgency, true),
			getFundingSources(userAgency, true),
			getWorkOrders(userAgency),
		]);
		setEmployees(empList);
		setFundingSources(fundingList);
		setWorkOrders(workOrderList);
	};

	useEffect(() => {
		handleSaveData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getVehicleList = () => {
		if (perPersonnelId) {
			dispatch(
				getVehicles(
					0,
					-1,
					"id",
					"asc",
					"",
					{ perPersonnelId: [{ value: perPersonnelId }] },
					userAgency,
				),
			);
		}
	};

	useEffect(() => {
		getVehicleList();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [perPersonnelId, dispatch]);

	const handleSaveVehicle = async (payload) => {
		const serviceReqIds = payload.serviceRequirements
			?.map((req) => {
				const findId = getCodeId(
					req,
					dictionary?.codePerVehicleServiceRequirements,
				);
				if (findId) return findId;
				return undefined;
			})
			.filter(Boolean);

		const data = {
			...payload,
			statusId: getCodeId(
				payload?.status,
				dictionary?.codePerVehicleStatus,
			),
			divisionId: getCodeId(
				payload?.division,
				dictionary?.codePerDepartment,
			),
		};
		try {
			if (selectedVehicle) {
				await dispatch(
					updateVehicle(
						selectedVehicle.id,
						data,
						serviceReqIds,
						userAgency,
						selectedVehicle,
					),
				);
			} else {
				await dispatch(
					createVehicle(
						data,
						serviceReqIds,
						userAgency,
						userData.user.ptsUserID,
					),
				);
			}
			setOpenDialog(false);
			setSelectedVehicle(null);
			getVehicleList();
		} catch (error) {
			dispatch(notify("Failed to save vehicle", "error"));
		}
	};

	const handleDelete = async () => {
		try {
			await dispatch(
				deleteVehicle(
					selectedId,
					userData.user.ptsUserID,
					selectedVehicle,
				),
			);
			setOpenDeleteDialog(false);
			setSelectedId(null);
			setSelectedVehicle(null);
		} catch (error) {
			dispatch(notify("Failed to delete vehicle", "error"));
		}
	};

	const vehicleSvcStatus = useMemo(() => {
		const map = {};
		vehicles.forEach((v) => {
			const statuses = (v.serviceRequirements || [])
				.map((reqId) => {
					const req =
						dictionary?.codePerVehicleServiceRequirements?.find(
							(sr) => sr.ptsCodeId === reqId || sr.id === reqId,
						);
					return req ? getServiceStatus(v, req, workOrders) : null;
				})
				.filter(Boolean);

			const worst =
				statuses.find((s) => s.status === "Overdue") ||
				statuses.find((s) => s.status === "Due Soon") ||
				statuses[0] ||
				null;

			map[v.id] = { worst, statuses };
		});
		return map;
	}, [vehicles, dictionary, workOrders]);

	const renderService = (row) => {
		const rowStatus = vehicleSvcStatus[row.id];
		const worst = rowStatus?.worst;
		if (worst) {
			const cls = worst.status === "Overdue" ? "overdue" : "due-soon";
			return <span className={`svc-badge ${cls}`}>{worst.status}</span>;
		}
		if (row?.serviceRequirements?.length > 0) {
			return <span className="svc-badge ok">OK</span>;
		}
		return (
			<span className="text3" style={{ fontSize: 11 }}>
				—
			</span>
		);
	};

	return (
		<>
			<Accordion
				expanded={expanded}
				onChange={() => setExpanded((prev) => !prev)}
				sx={{ mb: 1 }}
			>
				<AccordionSummary
					expandIcon={<ExpandMore />}
					sx={{
						backgroundColor: theme.accordion.backgroundColor,
						color: theme.accordion.textColor,
					}}
				>
					<Box
						display="flex"
						alignItems="center"
						justifyContent="space-between"
						width="100%"
					>
						<Box display="flex" alignItems="center">
							<DirectionsCar sx={{ mr: 1 }} />
							<span>Vehicles</span>
						</Box>
						<Box sx={{ mr: 2 }}>
							{canAddVehicle && (
								<PATAddButton
									tooltipText="Add Vehicle"
									onClick={() => {
										setOpenDialog(true);
                                        setExpanded((prev) => !prev);
									}}
								/>
							)}
						</Box>
					</Box>
				</AccordionSummary>
				<AccordionDetails>
					{vehicles?.length > 0 ? (
						<CustomTable
							columns={tableHeaders}
							data={vehicles}
							renderRow={(vehicle, index) => (
								<TableRow key={index}>
									<TableCell>
										{vehicle?.unitNumber || "-"}
									</TableCell>
									<TableCell>
										{`${vehicle?.year} ${vehicle?.make} ${vehicle?.model}` ||
											"-"}
									</TableCell>
									<TableCell>{vehicle?.vin || "-"}</TableCell>
									<TableCell>
										<VehicleStatusBadge
											status={capitalizeFirstLetter(
												getCodeDescription(
													vehicle?.status,
													dictionary.codePerVehicleStatus,
												),
											)}
											inService={
												vehicle?.[
													"statusInfo.InService"
												]
											}
										/>
									</TableCell>
									<TableCell>
										{vehicle?.odometer
											? `${vehicle?.odometer} mi`
											: "-"}
									</TableCell>
									<TableCell>
										{vehicle?.division
											? getCodeDescription(
													vehicle?.division,
													dictionary.codePerDepartment,
												)
											: "-"}
									</TableCell>
									<TableCell>
										{renderService(vehicle)}
									</TableCell>
									<TableCell>
										<Box display="flex" gap={1}>
											{canEditVehicle && (
												<PATEditButton
													tooltipText="Edit Vehicle"
													onClick={() => {
														setSelectedVehicle(
															vehicle,
														);
														setOpenDialog(true);
													}}
													size="20px"
												/>
											)}
											{canDeleteVehicle && (
												<PATDeleteButton
													tooltipText="Delete Vehicle"
													onClick={() => {
														setSelectedId(
															vehicle?.id,
														);
														setSelectedVehicle(
															vehicle,
														);
														setOpenDeleteDialog(
															true,
														);
													}}
													size="20px"
												/>
											)}
										</Box>
									</TableCell>
								</TableRow>
							)}
						/>
					) : (
						<span>No vehicles found.</span>
					)}
				</AccordionDetails>
			</Accordion>

			<DeleteConfirmationDialog
				open={openDeleteDialog}
				onClose={() => {
					setOpenDeleteDialog(false);
					setSelectedVehicle(null);
					setSelectedId(null);
				}}
				onConfirm={handleDelete}
				title="Delete Vehicle"
				message="Are you sure you want to delete this vehicle?"
			/>

			<AddEditVehicleDialog
				open={openDialog}
				onClose={() => {
					setOpenDialog(false);
					setSelectedVehicle(null);
				}}
				onSave={handleSaveVehicle}
				initialData={selectedVehicle}
				statusOptions={statusOptions}
				divisionOptions={departmentOptions}
				employeeOptions={employees}
				fundingSourceOptions={fundingSources}
				serviceRequirements={
					dictionary?.codePerVehicleServiceRequirements || []
				}
			/>
		</>
	);
};

export default VehiclesTable;
